/**
 * ClusterBackground - Visual grouping of nodes by cluster/topic
 *
 * Creates colored zones behind nodes that share the same cluster tag
 */

import React, { useMemo } from 'react';
import { useViewport } from '@xyflow/react';
import * as d3 from 'd3';

const ClusterBackground = ({ nodes, clusterColors }) => {
  const { x, y, zoom } = useViewport();

  // Group nodes by cluster and calculate hull boundaries
  const clusters = useMemo(() => {
    const grouped = {};

    nodes.forEach(node => {
      const tag = node.data?.cluster_tag;
      if (!tag || node.type === 'ghost' || node.type === 'challenge') return;

      if (!grouped[tag]) {
        grouped[tag] = {
          tag,
          color: clusterColors[tag] || { bg: 'rgba(99, 102, 241, 0.1)', border: '#6366f1' },
          points: [],
        };
      }

      // Add node position with padding
      const padding = 60;
      const pos = node.position;
      grouped[tag].points.push(
        [pos.x - padding, pos.y - padding],
        [pos.x + 200 + padding, pos.y - padding],
        [pos.x + 200 + padding, pos.y + 100 + padding],
        [pos.x - padding, pos.y + 100 + padding]
      );
    });

    // Calculate convex hull for each cluster
    return Object.values(grouped).map(cluster => {
      if (cluster.points.length < 3) {
        return { ...cluster, path: null };
      }

      try {
        const hull = d3.polygonHull(cluster.points);
        if (!hull) return { ...cluster, path: null };

        // Create smooth path from hull points
        const path = `M ${hull.map(p => p.join(',')).join(' L ')} Z`;
        return { ...cluster, path };
      } catch {
        return { ...cluster, path: null };
      }
    }).filter(c => c.path);
  }, [nodes, clusterColors]);

  if (clusters.length === 0) return null;

  return (
    <svg
      className="absolute inset-0 pointer-events-none"
      style={{
        transform: `translate(${x}px, ${y}px) scale(${zoom})`,
        transformOrigin: '0 0',
      }}
    >
      <defs>
        {clusters.map(cluster => (
          <filter
            key={`blur-${cluster.tag}`}
            id={`blur-${cluster.tag}`}
            x="-50%"
            y="-50%"
            width="200%"
            height="200%"
          >
            <feGaussianBlur in="SourceGraphic" stdDeviation="20" />
          </filter>
        ))}
      </defs>

      {clusters.map(cluster => (
        <g key={cluster.tag}>
          {/* Background blob */}
          <path
            d={cluster.path}
            fill={cluster.color.bg}
            stroke={cluster.color.border}
            strokeWidth={1}
            strokeOpacity={0.3}
            filter={`url(#blur-${cluster.tag})`}
          />

          {/* Cluster label (positioned at centroid) */}
          {cluster.points.length >= 6 && (
            <text
              x={d3.mean(cluster.points.map(p => p[0]))}
              y={d3.min(cluster.points.map(p => p[1])) - 20}
              fill={cluster.color.text || cluster.color.border}
              fontSize="12"
              fontWeight="500"
              textAnchor="middle"
              opacity={0.6}
            >
              {cluster.tag}
            </text>
          )}
        </g>
      ))}
    </svg>
  );
};

export default ClusterBackground;
